import { Product } from 'shared/@generated/models';
import { RatingStars } from 'shared/components/shop/rating-stars';
import { Text } from 'shared/components/text';
import { H2 } from 'shared/components/title';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';

type ProductReviewUser = {
  name?: string | null;
  short_name?: string | null;
};

type ProductRatingReview = {
  id: number;
  text?: string | null;
  translated_text?: string | null;
  created_at?: string | null;
  rating_score?: number | null;
  user?: ProductReviewUser | null;
};

type ProductWithRatingReviews = Product & {
  rating_reviews?: ProductRatingReview[] | null;
};

const clampRating = (rating: number) => Number(Math.max(1, Math.min(5, rating)).toFixed(1));

const formatRating = (rating: number) => rating.toFixed(1);

const decodeReviewText = (text?: string | null) => {
  if (!text) {
    return '';
  }

  return text
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/<[^>]+>/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const getProductReviews = (product: Product) => {
  const ratedProduct = product as ProductWithRatingReviews;
  const ratingReviews = (ratedProduct.rating_reviews ?? []) as ProductRatingReview[];

  return ratingReviews.flatMap((review) => {
    if (!review || typeof review.id !== 'number') {
      return [];
    }

    const body = decodeReviewText(review.translated_text ?? review.text);
    const rating =
      typeof review.rating_score === 'number' && Number.isFinite(review.rating_score)
        ? clampRating(review.rating_score)
        : undefined;
    const author = review.user?.short_name ?? review.user?.name ?? undefined;

    if (!body || rating === undefined) {
      return [];
    }

    return [
      {
        id: review.id,
        body,
        rating,
        author,
        createdAt: review.created_at ?? undefined,
      },
    ];
  });
};

export function ShopProductReviews({ product }: { product: Product }) {
  const { t, i18n } = useTranslation('screens');
  const reviews = getProductReviews(product);

  if (reviews.length === 0) {
    return null;
  }

  const dateFormatter = new Intl.DateTimeFormat(i18n.language, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  return (
    <>
      <H2 title={t('shop.shop_product.reviews')} />

      <View className={'grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-6'}>
        {reviews.map((review) => {
          const formattedDate = review.createdAt
            ? dateFormatter.format(new Date(review.createdAt))
            : undefined;

          return (
            <View
              key={review.id}
              className={'border-brand-brown-dark/15 rounded-2xl border bg-white p-4 md:p-5'}
            >
              <View className={'flex-row items-center justify-between gap-3'}>
                <View className={'flex-row items-center gap-2'}>
                  <RatingStars value={review.rating} size={'sm'} />

                  <Text className={'text-brand-brown-dark text-sm font-semibold tracking-tight'}>
                    {formatRating(review.rating)}
                  </Text>
                </View>

                {formattedDate && (
                  <Text className={'text-brand-brown-dark/55 text-xs md:text-sm'}>
                    {formattedDate}
                  </Text>
                )}
              </View>

              <Text className={'text-brand-brown-dark mt-3 text-base leading-7 md:text-lg'}>
                {review.body}
              </Text>

              {review.author && (
                <Text className={'text-brand-brown-dark/70 mt-4 text-sm font-semibold'}>
                  {review.author}
                </Text>
              )}
            </View>
          );
        })}
      </View>
    </>
  );
}
